<script setup lang="ts">

import {label} from "../../mixins/translations";
import {type ProductVariantResponse} from "../../../responses/ProductVariantResponse";
import {type ProductResponse} from "../../../responses/ProductResponse";
import {useProductStore} from "../../stores/product";
import {type Ref, ref} from "vue";
import {onMounted} from "vue";
import first from "lodash/first";

interface Props{
    product: ProductResponse
}
const props = defineProps<Props>();
const quantity: Ref<number> = ref(1);
const productStore = useProductStore();
const currentVariant: Ref<ProductVariantResponse> = ref(null);
const futureTime = (new Date()).setDate((new Date()).getDate() + 28); //4 weeks check

productStore.events.on("quantityChanged", (newQuantity) => {
    quantity.value = newQuantity;
})

onMounted(() => {
    currentVariant.value = first(props.product.variants);
});
</script>

<template>
    <ul class="mt-10 flex flex-col gap-2">
        <li v-if="currentVariant?.stock?.net_stock === 0">
            <div v-if="currentVariant?.stock.reserved_date">
                <div class="flex items-center gap-2" v-if="(new Date(currentVariant.stock.reserved_date)).getTime() <= futureTime">
                    <span class="w-3 h-3 rounded-xl bg-amber-500 mr-3"></span>
                    {{ label('site.product.stock.date', {'date': currentVariant?.stock.reserved_date}) }}
                </div>
                <div class="flex items-center gap-2" v-else>
                    <span class="w-3 h-3 rounded-xl bg-red-500 mr-3"></span>
                    {{ label('site.product.stock.date', {'date': currentVariant?.stock.reserved_date}) }}
                </div>
            </div>
            <div v-else>
                <div class="flex items-center gap-2">
                    <span class="w-3 h-3 rounded-xl bg-red-500 mr-3"></span>
                    {{ label('site.product.stock.out_of_stock') }}
                </div>
            </div>
        </li>
        <li v-else-if="currentVariant?.stock?.net_stock > 0">
            <div class="flex items-center gap-2" v-if="product.is_volume">
                <span class="w-3 h-3 rounded-xl bg-green-500 mr-3"></span>
                {{ label('site.product.stock.1-week') }}
            </div>
            <div class="flex items-center gap-2" v-else-if="quantity > currentVariant?.stock.net_stock">
                <span class="w-3 h-3 rounded-xl bg-yellow-500 mr-3"></span>
                {{ label('site.product.stock.1-2-weeks') }}
            </div>
            <div v-else class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-xl bg-green-500 mr-3"></span>
                {{ label('site.product.stock.1-2-days') }}
            </div>
        </li>
        <li class="flex items-center gap-2" v-else-if="product.is_volume">
            <span class="w-3 h-3 rounded-xl bg-amber-500 mr-3"></span>
            {{ label('site.product.stock.6-weeks') }}
        </li>
        <li class="flex items-center gap-2" v-else>
            <span class="w-3 h-3 rounded-xl bg-yellow-500 mr-3"></span>
            {{ label('site.product.stock.2-4-weeks') }}
        </li>

        <li class="flex items-center gap-3" v-if="product.is_volume">
            <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-truck w-5"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path><path d="M15 18H9"></path><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path><circle cx="17" cy="18" r="2"></circle><circle cx="7" cy="18" r="2"></circle></svg>
            </span>
            {{ label('site.product.volume_message') }}
        </li>

        <li class="flex items-center gap-3" v-if="product.return_code === 'NIET'">
            <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x w-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </span>
            {{ label('site.product.return_message') }}
        </li>
    </ul>
</template>
